import { baseTheme } from '@src/theme';
import { infoBoxVariants } from '@src/utilities/infoBoxVariants';
import clsx from 'clsx';
import { useMemo } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { twMerge } from 'tailwind-merge';
import { useTheme, withTheme } from '../ThemeProvider';
import { StyledAlert } from './styles';
import { InfoBoxProps } from './types';

const InfoBoxComponent = ({
    message,
    description,
    type = 'outlined',
    size = 'medium',
    icon,
    showIcon = true,
    className,
    action,
    ...props
}: Readonly<InfoBoxProps>) => {
    const { theme } = useTheme();

    const antdType = useMemo(() => {
        switch (type) {
            case 'filledSuccess':
                return 'success';
            case 'filledWarning':
                return 'warning';
            case 'danger':
            case 'filledDanger':
                return 'error';
            default:
                return 'info';
        }
    }, [type]);

    const getStyle = () => {
        const styles: React.CSSProperties = {};
        const isFilled = type.includes('filled');

        // Set background and text colors based on type
        if (type === 'outlined' || type === 'info') {
            styles.backgroundColor =
                theme?.colors?.info?.light?.surfaceBg ||
                baseTheme.colors.info.light.surfaceBg;
            styles.borderColor =
                theme?.colors?.info?.light?.border ||
                baseTheme.colors.info.light.border;
        } else if (type === 'success' || type === 'filledSuccess') {
            styles.backgroundColor = isFilled
                ? theme?.colors?.success?.main?.filled ||
                  baseTheme.colors.success.main.filled
                : theme?.colors?.success?.light?.surfaceBg ||
                  baseTheme.colors.success.light.surfaceBg;
            styles.borderColor = isFilled
                ? 'transparent'
                : theme?.colors?.success?.light?.border ||
                  baseTheme.colors.success.light.border;
        } else if (type === 'warning' || type === 'filledWarning') {
            styles.backgroundColor = isFilled
                ? theme?.colors?.warning?.main?.filled ||
                  baseTheme.colors.warning.main.filled
                : theme?.colors?.warning?.light?.surfaceBg ||
                  baseTheme.colors.warning.light.surfaceBg;
            styles.borderColor = isFilled
                ? 'transparent'
                : theme?.colors?.warning?.light?.border ||
                  baseTheme.colors.warning.light.border;
        } else if (type === 'danger' || type === 'filledDanger') {
            styles.backgroundColor = isFilled
                ? theme?.colors?.error?.main?.filled ||
                  baseTheme.colors.error.main.filled
                : theme?.colors?.error?.light?.surfaceBg ||
                  baseTheme.colors.error.light.surfaceBg;
            styles.borderColor = isFilled
                ? 'transparent'
                : theme?.colors?.error?.light?.border ||
                  baseTheme.colors.error.light.border;
        } else if (type === 'filledInfo') {
            styles.backgroundColor =
                theme?.colors?.info?.main?.filled ||
                baseTheme.colors.info.main.filled;
            styles.borderColor = 'transparent';
        }

        // Set text color for filled variants
        if (isFilled) {
            styles.color =
                theme?.colors?.secondary?.light?.text ||
                baseTheme.colors.secondary.light.text;
        }

        return styles;
    };

    const MainIcon = () => {
        return (
            icon || (
                <div
                    className={clsx('rounded-md', {
                        'p-3.5': size === 'large',
                        'p-3': size === 'medium',
                        'p-2': size === 'small',
                    })}
                    id="main-icon"
                >
                    {
                        <HiOutlineInformationCircle
                            size={size === 'small' ? 13 : 20}
                        />
                    }
                </div>
            )
        );
    };

    return (
        <StyledAlert
            {...props}
            icon={<MainIcon />}
            showIcon={showIcon}
            message={size === 'large' ? message : undefined}
            description={description}
            type={antdType}
            action={action}
            className={twMerge(
                type,
                infoBoxVariants({
                    className,
                    type: type,
                    size: size,
                })
            )}
            style={getStyle()}
        />
    );
};

export const InfoBox = withTheme(InfoBoxComponent);
